import React, { FC, ReactNode } from 'react';
import { prop, sortBy } from 'ramda';
import {
  Button,
  List,
  ListItemWithLabelsAndAction,
  Sticker,
  TrevorIcon,
} from '@trevor/ui-components';
import { TDatasource } from '@/types';
import DropdownItem from '@/util/DropdownItem';
import { ADMIN, OWNER, SUPER_ADMIN } from '@/constants';
import DropdownButton from '@/components/DropdownButton';
import MultiDropdown from '@/components/MultiDropdown';
import { useGetDatasourcesQuery } from '@/features/datasources/apiSlice';
import { DATASOURCE_OPTIONS } from '@/containers/DatasourcesPage/DatasourcesPage.utils';

const sortDataSources = sortBy<TDatasource>(prop('name'));

type DataSourceOptions = {
  datasource: TDatasource;
  selfHosted: boolean;
};

const divider = DropdownItem.divider();

const DataSourceOptions: FC<DataSourceOptions> = ({
  datasource,
  selfHosted,
}) => {
  const openDataSourceLink = (target: string) =>
    window.open(`/datasources/${datasource.id}/${target}`);

  const editConnection = DropdownItem.action(<span>Edit connection</span>, () =>
    openDataSourceLink('setup'),
  );
  const configureDatasource = selfHosted
    ? null
    : DropdownItem.action(<span>Configure table access</span>, () =>
        openDataSourceLink('configure/tables'),
      );
  const billingLink = selfHosted
    ? null
    : DropdownItem.action(<span>Manage billing</span>, () =>
        openDataSourceLink('billing'),
      );

  const deleteDatasource = DropdownItem.action(
    <span>Delete datasource</span>,
    () => openDataSourceLink('delete'),
  );

  let dropdownItems: ReactNode[] = [];

  if (datasource.role === OWNER) {
    dropdownItems = [
      editConnection,
      configureDatasource,
      billingLink,
      divider,
      deleteDatasource,
    ];
  } else if (selfHosted && datasource.role === SUPER_ADMIN) {
    dropdownItems = [
      editConnection,
      configureDatasource,
      divider,
      deleteDatasource,
    ];
  } else if (datasource.role === ADMIN) {
    dropdownItems = [billingLink];
  }

  if (dropdownItems.filter(Boolean).length === 0) {
    return null;
  }
  return (
    <DropdownButton
      stopPropagation
      // @ts-expect-error: TODO: get rid of MultiDropdown.
      contentBuilder={() => <MultiDropdown items={dropdownItems} />}
    >
      <Button
        style={{ marginLeft: '16px' }}
        variant="secondary"
        size="small"
        icon={<TrevorIcon name="menu-horizontal" />}
      />
    </DropdownButton>
  );
};

export const DataSourceList = () => {
  const { data } = useGetDatasourcesQuery();

  if (!data) return null;

  const { datasources, selfHosted } = data;

  const datasourcesSorted = sortDataSources(datasources);

  return (
    <List>
      {datasourcesSorted.map((datasource) => {
        const dbType = DATASOURCE_OPTIONS.find(
          (db) => datasource.dbType === db.enum,
        );
        const dbIcon = dbType?.iconName;
        const dbName = dbType?.name;

        // const handleRedirectToDatasource = () => navigate(datasource.id);
        // TODO use navigate once we found out how to re-inject Bento config properly
        const handleRedirectToDatasource = () =>
          (window.location.href = `/datasources/${datasource.id}`);

        const labels = [
          datasource.isHeroku && (
            <Sticker
              key="heroku"
              iconName="heroku"
              tooltipContent="Added via Heroku"
            />
          ),
          <Sticker
            key="type"
            iconName={dbIcon}
            tooltipContent={`Database type: ${dbName}`}
          />,
          <Sticker
            key="role"
            label={datasource.role}
            tooltipContent={`Your role: ${datasource.role}`}
          />,
        ];

        return (
          <ListItemWithLabelsAndAction
            key={datasource.id}
            onClick={handleRedirectToDatasource}
            title={
              <>
                {datasource.name}
                <DataSourceOptions
                  datasource={datasource}
                  selfHosted={selfHosted}
                />
              </>
            }
            actionLabel="Click to launch"
            labels={labels}
          />
        );
      })}
    </List>
  );
};
